import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, Check, Send, MessageSquare, Share2, RefreshCw, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const buildInviteMessage = (companyName, name, link) => {
  const greeting = name ? `Hi ${name.split(' ')[0]}, ` : '';
  return `${greeting}${companyName || 'we'} invited you to drive with us on CivilX TMS. Tap to start your application (no password needed, link expires in 7 days): ${link}`;
};

export const buildMailto = (email, name, link, companyName, senderName) => {
  const company = companyName || 'our team';
  const subject = `You're invited to drive for ${company} — CivilX TMS onboarding`;
  const body = `${name ? `Hi ${name},` : 'Hello,'}

You've been invited to join ${company} as a driver on the CivilX TMS platform.

Please click the link below to set up your account and complete your driver application:

${link}

If you have any questions, just reply to this email.

Thank you,
${senderName || 'Dispatch Team'}`;
  return `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

const btn = 'inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold transition-all no-underline';

// Copy / Email / Text / Share / Resend actions for one invitation. Works in the native shell:
// sms: and mailto: open the phone's apps; navigator.share opens the iOS/Android share sheet.
export const InviteShareActions = ({ invite, companyName, senderName, onResent, compact = false }) => {
  const [copied, setCopied] = useState(false);
  const [resending, setResending] = useState(null);
  const message = buildInviteMessage(companyName, invite.name, invite.invite_link);
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const copy = async () => {
    try { await navigator.clipboard.writeText(invite.invite_link); } catch { /* clipboard blocked */ }
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };
  const share = async () => {
    try { await navigator.share({ title: 'CivilX driver invitation', text: message, url: invite.invite_link }); } catch { /* cancelled */ }
  };
  const resend = async (channel) => {
    setResending(channel);
    try {
      const res = await axios.post(`${API}/driver-invitations/${invite.id}/resend`,
        { send_email: channel === 'email', send_sms: channel === 'sms' }, { withCredentials: true });
      if (channel === 'email') res.data.email_sent ? toast.success(`Invite emailed to ${invite.email}`) : toast.error(res.data.email_error || 'Email could not be sent');
      if (channel === 'sms') res.data.sms_sent ? toast.success(`Invite texted to ${invite.phone}`) : toast.error(res.data.sms_error || 'Text could not be sent');
      onResent?.(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Resend failed');
    } finally {
      setResending(null);
    }
  };

  const dark = compact ? 'text-[#002FA7] hover:text-[#00247D] px-1.5 py-1' : 'bg-white border border-slate-300 text-slate-800 hover:bg-slate-50';
  return (
    <div className={`flex flex-wrap items-center ${compact ? 'gap-1' : 'gap-2'}`} data-testid={`invite-actions-${invite.id}`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      <button type="button" onClick={copy} data-testid={`copy-link-${invite.id}`} className={`${btn} ${dark}`}>
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}{copied ? 'Copied' : 'Copy link'}
      </button>
      <button type="button" onClick={() => resend('email')} disabled={!!resending} data-testid={`resend-email-${invite.id}`} className={`${btn} ${dark} disabled:opacity-50`} title="Re-send the branded invitation email from CivilX">
        {resending === 'email' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}Email
      </button>
      {invite.phone && (
        <button type="button" onClick={() => resend('sms')} disabled={!!resending} data-testid={`resend-sms-${invite.id}`} className={`${btn} ${dark} disabled:opacity-50`} title="Send the invite link by text message from CivilX">
          {resending === 'sms' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}Text
        </button>
      )}
      {invite.phone && (
        <a href={`sms:${invite.phone}${/iPhone|iPad|Mac/.test(navigator.userAgent) ? '&' : '?'}body=${encodeURIComponent(message)}`} data-testid={`sms-app-link-${invite.id}`} className={`${btn} ${dark}`} title="Open your Messages app with the invite pre-filled">
          <MessageSquare className="w-3.5 h-3.5" />My phone
        </a>
      )}
      <a href={buildMailto(invite.email, invite.name, invite.invite_link, companyName, senderName)} data-testid={`email-link-${invite.id}`} className={`${btn} ${dark}`} title="Open your mail app with the invite pre-filled">
        <Send className="w-3.5 h-3.5" />My mail
      </a>
      {canShare && (
        <button type="button" onClick={share} data-testid={`share-invite-${invite.id}`} className={`${btn} bg-[#002FA7] text-white hover:bg-[#00247D]`}>
          <Share2 className="w-3.5 h-3.5" />Share…
        </button>
      )}
      {!compact && <RefreshCw className="hidden" />}
    </div>
  );
};

export const DeliveryChips = ({ invite }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={`delivery-chips-${invite.id}`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${invite.email_sent ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
      Email {invite.email_sent ? 'sent' : 'not sent'}
    </span>
    {invite.phone && (
      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${invite.sms_sent ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`} title={invite.sms_error || ''}>
        Text {invite.sms_sent ? 'sent' : 'failed'}
      </span>
    )}
    {'opened_at' in invite && (
      invite.opened_at ? (
        <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-blue-100 text-blue-800" data-testid={`invite-opened-${invite.id}`} title={new Date(invite.opened_at).toLocaleString()}>
          Opened
        </span>
      ) : (
        <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-slate-100 text-slate-600" data-testid={`invite-unopened-${invite.id}`}
              title={invite.reminder_count ? `Last reminder ${new Date(invite.last_reminder_at).toLocaleString()}` : 'Auto-reminder goes out 2 days after sending if the link is not opened'}>
          Not opened{invite.reminder_count ? ` · ${invite.reminder_count} reminder${invite.reminder_count === 1 ? '' : 's'}` : ''}
        </span>
      )
    )}
  </div>
);
